import * as AmplitudeBrowser from "@amplitude/analytics-browser";
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
  createTheme
} from "@mui/material/styles";
import autoBind from "auto-bind";
import classNames from "classnames";
import Cookies from "js-cookie";
import qs from "qs";
import React from "react";
import { Helmet } from "react-helmet";
import { Router, useLocation } from "react-router";
import { renderRoutes } from "react-router-config";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";

import config from "../config";
import PageHelper from "../lib/PageHelper";
import createSanitizeAnalyticsUrlPlugin from "../lib/analytics/createSanitizeAnalyticsUrlPlugin";
import { hasTvFullScreenQuery } from "../lib/tvFullScreenQuery";
import withBottomNavigation from "../hocs/withBottomNavigation";
import withConsentSettingsWeb from "../hocs/withConsentSettingsWeb";
import withOverlay from "../hocs/withOverlay";
import withPaymentDetails from "../hocs/withPaymentDetails";
import withUserData from "../hocs/withUserData";
import withWebNetworkStatus from "../hocs/withWebNetworkStatus";
import { NetworkStatus } from "../hooks/useWebNetworkStatus";
import { connect } from "../i18n/connect";
import AnalyticsEventTracker from "../lib/AnalyticsEventTracker";
import getIntercomUserData from "../lib/Intercom/getIntercomUserData";
import AnalyticsEventTrackerWeb from "../lib/AnalyticsEventTracker/AnalyticsEventTrackerWeb";
import { getIsMobile } from "../lib/platformHelper";
import { showModal } from "../lib/react-redux-modal-provider";
import StackableModalProvider from "../lib/react-redux-modal-provider/StackableModalProviderWeb";
import SocketClient from "../lib/SocketIO/SocketClient";
import { actionHandlerRoutine } from "../redux/modules/actionHandler";
import { fetchAnalysisCategories } from "../redux/modules/analysis/analysisCategories";
import { fetchInsiderAnalysisCategories } from "../redux/modules/analysis/insiderAnalysisCategories";
import { actions as authActions } from "../redux/modules/auth/login";
import { getUserID } from "../redux/modules/auth/login/selector";
import { setInitialAuthCheckPending } from "../redux/modules/initialAuthCheck";
import { PLAN_PERMISSIONS_NAMES } from "../redux/modules/auth/userPermissions/selector";
import { followedStocksRoutine } from "../redux/modules/followed";
import { fetchGlobalConfig } from "../redux/modules/globalConfig";
import { fetchManualStrategies } from "../redux/modules/manualStrategies";
import { fetchMarketingBarsRoutine } from "../redux/modules/marketingBars";
import { fetchMarketingMessagesRoutine } from "../redux/modules/marketingMessages";
import { shouldShowMarketingMessageBar } from "../redux/modules/marketingMessages/selector";
import { actions as notificationsActions } from "../redux/modules/notifications";
import { fetchRecommendedStocksRoutine } from "../redux/modules/recommendedStocks";
import { getAppStatus } from "../redux/modules/status/selector";
import { getLocale } from "../redux/modules/translations/selector";
import { initTagsServiceRoutine } from "../redux/modules/tagsService";
import { actions as translationsActions } from "../redux/modules/translations";
import { fetchUserMessagesRoutine } from "../redux/modules/userMessages";
import {
  addNotification as addNotificationNew,
  markAsRead as markAsReadNew
} from "../redux/modules/newNotifications";
import muiTheme from "../theme/mui/mui_theme";
import Template from "./components/BaseTemplate";
import ActionInProgress from "./components/common/ActionInProgress";
import PaymentFormLoadingProvider from "./components/common/PaymentFormLoadingProvider";
import AmplitudePageViewHandler from "./components/common/AmplitudePageViewHandler/modules/default";
import AppErrorStateView from "./components/common/AppErrorStateView/modules/default";
import BrowserNotification from "./components/common/BrowserNotification";
import CanonicalTagProvider from "./components/common/CanonicalTagProvider";
import EmailConfirmationSnackbar from "./components/common/EmailConfirmationSnackbar";
import FloatingInformation from "./components/common/FloatingInformation";
import LanguageHelper from "./components/common/LanguageHelper";
import MarketingBars from "./components/common/MarketingBars";
import MarketingParamsProvider from "./components/common/MarketingParamsProvider";
import CreatePriceAlertProvider from "./components/common/CreatePriceAlertProvider";
import IntercomVisibilityProvider from "./components/common/IntercomVisibilityProvider";
import NetworkStatusProvider from "./components/common/NetworkStatusProvider/modules/default";
import ScrollToTop from "./components/common/ScrollToTop";
import StaticPageRedirectGuard from "./components/common/StaticPageRedirectGuard";
import UserActionsProvider from "./components/common/UserActionsProvider";
import UserMessagesHandler from "./components/common/UserMessagesHandler";
import ErrorPage from "./components/pages/ErrorPage";
import Page404 from "./components/pages/Page404";
import routes from "./routes";

const mapStateToProps = state => ({
  userID: getUserID(state),
  appStatus: getAppStatus(state),
  shouldShowMarketingMessageBar: shouldShowMarketingMessageBar(state),
  locale: getLocale(state)
});

const mapDispatchToProps = {
  addNotification: notificationsActions.addNotification,
  addNotificationNew: addNotificationNew,
  markAsRead: notificationsActions.markAsRead,
  markAsReadNew: markAsReadNew,
  fetchTranslations: translationsActions.fetchTranslations,
  fetchNotifications: notificationsActions.fetchNotifications,
  fetchMarketTranslations: translationsActions.fetchMarketTranslations,
  cookieLogin: authActions.cookieLogin,
  setInitialAuthCheckPending,
  fetchFollowedStocks: followedStocksRoutine,
  fetchMarketingBars: fetchMarketingBarsRoutine,
  actionHandler: actionHandlerRoutine,
  fetchRecommendedStocks: fetchRecommendedStocksRoutine,
  fetchUserMessages: fetchUserMessagesRoutine,
  fetchMarketingMessages: fetchMarketingMessagesRoutine,
  initTagsService: initTagsServiceRoutine,
  fetchAnalysisCategories,
  fetchInsiderAnalysisCategories,
  fetchManualStrategies,
  fetchGlobalConfig
};

@connect(mapStateToProps, mapDispatchToProps)
@withBottomNavigation("navState")
@withUserData
@withPaymentDetails
@withOverlay
@withWebNetworkStatus
@withConsentSettingsWeb
class RootComponent extends React.Component {
  constructor(props) {
    super(props);

    autoBind.react(this);

    this.state = {
      currentNotification: {
        title: "",
        body: "",
        callback: () => {}
      }
    };

    if (config.amplitude.enabled) {
      AmplitudeBrowser.add(createSanitizeAnalyticsUrlPlugin());

      this.Amplitude = AmplitudeBrowser.init(config.amplitude.apiKey, null, {
        includeReferrer: true,
        includeUtm: true,
        includeGclid: true,
        defaultTracking: true
      });
    }

    this.intercomInitialized = false;
  }

  getWebSocketRoomName(param) {
    return "user_id_" + param;
  }

  async componentDidMount() {
    this.handleActionFromUrl();
    this.handlePremiumBenefitsModal();

    window.gtag =
      window.gtag ||
      function() {
        window.dataLayer.push(arguments);
      };

    const {
      fetchTranslations,
      fetchNotifications,
      fetchRecommendedStocks,
      fetchFollowedStocks,
      userID,
      cookieLogin,
      setInitialAuthCheckPending,
      refreshUserDataPromiseCreator,
      fetchMarketingBars,
      fetchGlobalConfig,
      fetchUserMessages,
      fetchMarketingMessages,
      userData,
      refreshUserData,
      fetchPaymentDetails,
      fetchAnalysisCategories,
      fetchInsiderAnalysisCategories,
      fetchManualStrategies,
      initTagsService,
      fetchMarketTranslations,
      hasPermission
    } = this.props;

    fetchGlobalConfig();

    const urlParams = qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    });

    const shouldRedirectToNativeAfterPayment = localStorage.getItem(
      config.shouldRedirectToNativeAfterPaymentStorageKey
    );

    if (
      shouldRedirectToNativeAfterPayment &&
      (urlParams.after_payment || urlParams.status === "PERFORMED") &&
      config.nativeAppScheme
    ) {
      localStorage.removeItem(
        config.shouldRedirectToNativeAfterPaymentStorageKey
      );
      window.location =
        config.nativeAppScheme + "/userSettings?defaultCategory=payments";
    }

    if (!shouldRedirectToNativeAfterPayment && urlParams.after_payment) {
      setTimeout(() => {
        refreshUserData();
        fetchPaymentDetails();
      }, 1000);

      setTimeout(() => {
        refreshUserData();
        fetchPaymentDetails();
      }, 5000);

      setTimeout(() => {
        refreshUserData();
        fetchPaymentDetails();
      }, 10000);
    }

    const authTokenFromUrl = urlParams.auth_token_for_login;
    const willRedirectToNativeAfterPayment =
      urlParams.will_redirect_to_native_after_payment;

    if (willRedirectToNativeAfterPayment) {
      localStorage.setItem(
        config.shouldRedirectToNativeAfterPaymentStorageKey,
        "true"
      );
    }

    if (authTokenFromUrl) {
      cookieLogin(authTokenFromUrl, !!userID);
    } else if (userID) {
      try {
        await refreshUserDataPromiseCreator();
        fetchUserMessages();
      } catch (error) {
      } finally {
        setInitialAuthCheckPending(false);
      }
    } else {
      const authTokenFromCookie = Cookies.get(config.authTokenCookieName);

      if (authTokenFromCookie) {
        cookieLogin(authTokenFromCookie);
      } else {
        setInitialAuthCheckPending(false);
      }
    }

    initTagsService();
    fetchTranslations();
    fetchMarketTranslations();
    fetchNotifications();
    fetchMarketingBars();
    fetchRecommendedStocks();
    fetchMarketingMessages();

    if (hasPermission([PLAN_PERMISSIONS_NAMES.FOLLOW])) {
      fetchFollowedStocks();
    }
    fetchPaymentDetails();
    fetchAnalysisCategories();
    fetchInsiderAnalysisCategories();
    fetchManualStrategies();

    if (userID) {
      AmplitudeBrowser.setUserId(userID);

      SocketClient.init(this.props.userID);

      SocketClient.connect(
        config.socketIoKeys.notifications,
        this.handleNotification,
        this.getWebSocketRoomName(userID)
      );
    } else {
      const identify = new AmplitudeBrowser.Identify();

      identify.set("user_status", "visitor");

      AmplitudeBrowser.identify(identify);
    }

    AnalyticsEventTrackerWeb.attemptTrackRegisterConversionUsingGTM();

    let link =
      document.querySelector("link[rel*='icon']") ||
      document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = `/favicon_${config.vendor}.png`;
    document.getElementsByTagName("head")[0].appendChild(link);

    this.handleIntroductionModal();

    if (userData && !userData.email_validated) {
      this.refreshUserDataInterval = setInterval(() => {
        refreshUserData({ skipSettingGtmUserData: true });
      }, 10000);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { userData, fetchPaymentDetails, paymentDetails } = this.props;

    const userLoginStateChanged = prevProps.userID !== this.props.userID;
    const userPaymentDetailsChanged = !isEqual(
      prevProps.paymentDetails,
      this.props.paymentDetails
    );

    if (userLoginStateChanged) {
      SocketClient.init(this.props.userID);

      SocketClient.connect(
        config.socketIoKeys.notifications,
        this.handleNotification,
        this.getWebSocketRoomName(this.props.userID)
      );

      fetchPaymentDetails();
    }

    const { history, locale } = this.props;
    const isAuthPage = PageHelper.getIsAuthPage(history.location, locale);

    if (
      !isAuthPage &&
      window.Intercom &&
      (userLoginStateChanged ||
        userPaymentDetailsChanged ||
        !this.intercomInitialized)
    ) {
      const intercomUserData = getIntercomUserData(
        userData,
        null,
        paymentDetails
      );

      if (!isEmpty(intercomUserData)) {
        window.Intercom("update", intercomUserData);
      } else {
        window.Intercom("shutdown");
        window.Intercom("boot");
      }

      this.intercomInitialized = true;
    }

    if (!userData || userData.email_validated) {
      clearInterval(this.refreshUserDataInterval);
      this.refreshUserDataInterval = null;
    }
  }

  componentWillUnmount() {
    SocketClient.disconnect(this.getWebSocketRoomName(this.props.userID));
  }

  handleNotification(data) {
    this.props.addNotificationNew(data);
    this.prepareNotification(data);
  }

  prepareNotification(data) {
    const { markAsReadNew } = this.props;

    this.setState({
      currentNotification: {
        title: config.title,
        body: data.message,
        notificationMetaData: data,
        callback: () => {
          markAsReadNew({ id: data.id });
        }
      }
    });
  }

  handleActionFromUrl() {
    const { actionHandler } = this.props;
    const { search } = window.location;

    const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

    if (searchParams.action) {
      actionHandler(searchParams);
    }
  }

  handleIntroductionModal() {
    const { locale } = this.props;
    const location = { pathname: window.location.pathname };

    if (PageHelper.getIsOfferCheckoutPage(location, locale)) {
      return;
    }

    if (localStorage.getItem(config.introductionModalStorageKey)) {
      localStorage.removeItem(config.introductionModalStorageKey);

      if (getIsMobile()) {
        showModal("IntroductionModal");
      }
    }
  }

  handlePremiumBenefitsModal() {
    const { search } = window.location;

    const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

    if (searchParams.premium_benefits) {
      showModal("PremiumBenefitsModal");
    }
  }

  render() {
    const {
      userData,
      shouldShowMarketingMessageBar,
      navState,
      history,
      appStatus: { error, errorStatus, userDataUpdateReturnedUnexpectedError },
      disabledOverlay,
      networkStatus
    } = this.props;

    const { currentNotification } = this.state;

    let appClasses = classNames({
      "app-content": true,
      "opened-bottom-nav": !!navState,
      [`opened-${navState}`]: !!navState,
      "payment-alert": shouldShowMarketingMessageBar
    });

    if (userDataUpdateReturnedUnexpectedError) {
      return <AppErrorStateView />;
    }

    const hasEmailSnackbar =
      userData && !userData.email_validated && !disabledOverlay;

    return (
      <React.Fragment>
        <Helmet>
          <meta name="robots" content="index, follow" />
          <meta
            name="googlebot"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
          <meta
            name="bingbot"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
        </Helmet>
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={createTheme(muiTheme)}>
            <Router history={history}>
              <CreatePriceAlertProvider />
              <IntercomVisibilityProvider />
              <Wrapper
                appClasses={appClasses}
                hasEmailSnackbar={hasEmailSnackbar}
                currentNotification={currentNotification}
                networkStatus={networkStatus}
                errorStatus={errorStatus}
                error={error}
              />
            </Router>
          </MuiThemeProvider>
        </StyledEngineProvider>
        <ActionInProgress />
        <PaymentFormLoadingProvider />
      </React.Fragment>
    );
  }
}

const Wrapper = ({
  appClasses,
  hasEmailSnackbar,
  currentNotification,
  networkStatus,
  errorStatus,
  error
}) => {
  const { pathname, search } = useLocation();

  const isLandingPage = ["/", "/pl", "/en"].includes(pathname);
  const isTvFullScreen =
    hasTvFullScreenQuery(search) || /\/fullscreenchart\//.test(pathname);

  const content = (
    <>
      {networkStatus === NetworkStatus.OFFLINE ? null : error ? (
        errorStatus === 404 ? (
          <Page404 />
        ) : (
          <ErrorPage />
        )
      ) : (
        <StaticPageRedirectGuard>
          {renderRoutes(routes)}
        </StaticPageRedirectGuard>
      )}
      <NetworkStatusProvider />
    </>
  );

  return (
    <ScrollToTop>
      <CanonicalTagProvider />
      <div
        className={
          isLandingPage
            ? undefined
            : classNames(appClasses, {
                "tv-full-screen": isTvFullScreen
              })
        }
      >
        {hasEmailSnackbar ? <EmailConfirmationSnackbar /> : null}
        <FloatingInformation />
        <BrowserNotification data={currentNotification} />
        {isLandingPage ? content : <Template>{content}</Template>}
        <StackableModalProvider />
        <UserActionsProvider />
        <MarketingBars />
        <UserMessagesHandler />
        <LanguageHelper />
        <AmplitudePageViewHandler />
        <MarketingParamsProvider />
      </div>
    </ScrollToTop>
  );
};

export default RootComponent;
